import { useEffect, useState } from 'react';
import { LogIn } from 'lucide-react';
import { api } from '../api';
import { useSession } from '../auth/session';

/**
 * Sign-in screen. In production this just bounces to /auth/login →
 * Google → /auth/callback. In AUTH_DEV_MODE the page also surfaces a
 * dropdown of the emails listed in DEV_ROLE_MAP so we can preview
 * the dashboard as each role without finishing GCP setup.
 */
export default function SignIn() {
  const { reload } = useSession();
  const [devUsers, setDevUsers] = useState(null); // null = unknown, [] = disabled
  const [picked, setPicked] = useState('');
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.auth.devRoles()
      .then((d) => {
        setDevUsers(d.enabled ? d.users : []);
        if (d.enabled && d.users.length) setPicked(d.users[0].email);
      })
      .catch(() => setDevUsers([]));
  }, []);

  const devMode = devUsers && devUsers.length > 0;

  const doDevLogin = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.auth.devLogin(picked);
      await reload();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-950 px-6">
      <div className="w-full max-w-md bg-slate-800 border border-slate-700 rounded-2xl p-8 shadow-sm">
        <div className="flex items-center gap-2 mb-6">
          <span className="text-slate-100 text-xl font-bold tracking-tight">P45</span>
          <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: '#e92030' }} />
        </div>

        <h1 className="text-2xl font-semibold text-slate-100 tracking-tight">Commercial Silo</h1>
        <p className="text-sm text-slate-400 mt-1.5 leading-relaxed">
          Internal dashboard. Sign in with your Platform45 Google account.
          Access is granted via Workspace group membership.
        </p>

        <a
          href={api.auth.loginUrl()}
          className="mt-7 inline-flex items-center justify-center gap-2 w-full bg-brand-500 hover:bg-brand-600 text-white text-sm font-medium rounded-md px-4 py-2.5 transition-colors"
        >
          <LogIn className="w-4 h-4" strokeWidth={1.75} />
          Continue with Google
        </a>

        {devMode && (
          <div className="mt-7 pt-6 border-t border-slate-700">
            <p className="text-[11px] font-medium uppercase tracking-[0.12em] text-amber-600 mb-3">
              Dev mode · pretend to be
            </p>
            <div className="flex gap-2">
              <select
                value={picked}
                onChange={(e) => setPicked(e.target.value)}
                className="flex-1 bg-slate-900 border border-slate-700 rounded-md px-3 py-2 text-sm text-slate-100"
              >
                {devUsers.map((u) => (
                  <option key={u.email} value={u.email}>
                    {u.email} · {u.role}
                  </option>
                ))}
              </select>
              <button
                onClick={doDevLogin}
                disabled={busy}
                className="px-3 py-2 rounded-md text-sm font-medium bg-slate-700 hover:bg-slate-600 text-slate-100 disabled:opacity-50"
              >
                {busy ? '…' : 'Enter'}
              </button>
            </div>
            <p className="text-[11px] text-slate-500 mt-3">
              Edit <code className="text-slate-400">DEV_ROLE_MAP</code> in <code className="text-slate-400">.env</code> to add or remove dev users.
            </p>
          </div>
        )}

        {error && (
          <p className="mt-4 text-sm text-brand-500">{error}</p>
        )}
      </div>
    </div>
  );
}
